import JSZip from 'jszip';
import { unparse } from 'papaparse';
import { InstanceCsvRepository } from '../csv/instance/services/InstanceCsvRepository';
import { OrganizationCsvRepository } from '../csv/organization/services/OrganizationCsvRepository';
import { ProjectCsvRepository } from '../csv/project/services/ProjectCsvRepository';
import { ReportCsvRepository } from '../csv/report/services/ReportCsvRepository';
import { InstanceExportOptions } from '../options/InstanceExportOptions';
import { OrganizationExportOptions } from '../options/OrganizationExportOptions';
import { ProjectExportOptions } from '../options/ProjectExportOptions';
import { ReportExportOptions } from '../options/ReportExportOptions';
import { formatExportName } from '../utils/exportNameUtils';

type FileDescriptor = {
  name: string;
  content: BlobPart;
};

const DEFAULT_CSV_CONFIG: Papa.UnparseConfig = {
  // See https://www.papaparse.com/docs#json-to-csv for config documentation
  quotes: false,
  quoteChar: '"',
  escapeChar: '"',
  delimiter: ',',
  header: true,
  newline: '\r\n',
  skipEmptyLines: false,
  columns: undefined,
};

export class CsvExportManager {
  private readonly organizationSummaryRepository = new OrganizationCsvRepository();
  private readonly projectSummaryRepository = new ProjectCsvRepository();
  private readonly reportSummaryRepository = new ReportCsvRepository();
  private readonly instanceRepository = new InstanceCsvRepository();

  async exportOrganizationDashboard(organizationName: string, options: OrganizationExportOptions): Promise<void> {
    const summary = await this.organizationSummaryRepository.getTrendsOrganization(options);

    const baseFiles = [
      { content: summary.getOverview(), exportType: 'overview' },
      { content: summary.getInventory(), exportType: 'inventory' },
      { content: summary.getIssues(), exportType: 'issues' },
      { content: summary.getTrends(), exportType: 'trends' },
    ];

    const files: FileDescriptor[] = baseFiles.map(({ content, exportType }) => ({
      name: formatExportName(organizationName, { exportType, extension: 'csv' }),
      content: unparse({ fields: content.getHeaders(), data: content.getData() }, DEFAULT_CSV_CONFIG),
    }));

    await this.zipAndDownload(formatExportName(organizationName, { extension: 'zip' }), files);
  }

  async exportProjectDashboard(projectId: number, projectName: string, options: ProjectExportOptions): Promise<void> {
    const summary = await this.projectSummaryRepository.getProject(projectId, options);

    const files: FileDescriptor[] = [
      { content: summary.getInventory(), exportType: 'inventory' },
      { content: summary.getIssues(), exportType: 'issues' },
    ].map(({ content, exportType }) => ({
      name: formatExportName(projectName, { exportType, extension: 'csv' }),
      content: unparse({ fields: content.getHeaders(), data: content.getData() }, DEFAULT_CSV_CONFIG),
    }));

    await this.zipAndDownload(formatExportName(projectName, { extension: 'zip' }), files);
  }

  async exportReport(reportId: number, reportName: string, options: ReportExportOptions): Promise<void> {
    const summary = await this.reportSummaryRepository.getReport(reportId, options);

    const files: FileDescriptor[] = [
      { content: summary.getInventory(), exportType: 'inventory' },
      { content: summary.getIssues(), exportType: 'issues' },
      { content: summary.getInstances(), exportType: 'instances' },
      { content: summary.getObjectStats(), exportType: 'statistics' },
    ].map(({ content, exportType }) => ({
      name: formatExportName(reportName, { exportType, extension: 'csv' }),
      content: unparse({ fields: content.getHeaders(), data: content.getData() }, DEFAULT_CSV_CONFIG),
    }));

    await this.zipAndDownload(formatExportName(reportName, { extension: 'zip' }), files);
  }

  async exportInstance(instanceName: string, options: InstanceExportOptions): Promise<void> {
    const summary = await this.instanceRepository.getInstance(options);

    const files: FileDescriptor[] = [
      { content: summary.getInstance(), exportType: 'instance' },
      { content: summary.getIssue(), exportType: 'issue' },
      { content: summary.getCalls(), exportType: 'calls' },
      { content: summary.getOperations(), exportType: 'operations' },
      { content: summary.getLocations(), exportType: 'locations' },
      { content: summary.getHardCodedStringLocations(), exportType: 'hard_coded_string_locations' },
    ]
      // For a instance export, the "instance" and "issue" files will always exist with one row.
      // The rest of the content depends on the instance and its origin trace type.
      // Only zip files that have at least one row to avoid inaccurate information.
      .filter(({ content }) => content.getData().length)
      .map(({ content, exportType }) => ({
        name: formatExportName(instanceName, { exportType, extension: 'csv' }),
        content: unparse({ fields: content.getHeaders(), data: content.getData() }, DEFAULT_CSV_CONFIG),
      }));

    await this.zipAndDownload(formatExportName(instanceName, { extension: 'zip' }), files);
  }

  private async zipAndDownload(filename: string, files: FileDescriptor[]) {
    const zip = new JSZip();
    for (const file of files) {
      zip.file(file.name, new Blob([file.content], { type: 'application/csv;charset=utf-8' }));
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(content);

    this.download(filename, url);
  }

  private download(filename: string, objectUrl: string) {
    const a = document.createElement('a');
    a.setAttribute('style', 'display: none');
    a.href = objectUrl;
    a.download = filename;

    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(objectUrl);
    a.remove();
  }
}
